import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Plus, 
  RefreshCw, 
  Clock, 
  AlertCircle,
  ClipboardList,
  CheckSquare,
  Loader,
  Calendar,
  Target,
  User
} from 'lucide-react';
import axiosInstance from '../../lib/api/axiosInstance';
import { TaskTable } from './TaskTable';
import { TaskKanban } from './TaskKanban';
import { TaskEditDialog } from './TaskEditDialog';
import { Button } from '../ui/button';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription,
} from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Progress } from '../ui/progress';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { Task, TaskStatus } from '../../types/task';

interface TaskStats {
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  todoTasks: number;
  completedThisWeek: number;
  upcomingDueTasks: number;
}

export const TaskDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<TaskStats>({
    totalTasks: 0,
    completedTasks: 0,
    inProgressTasks: 0,
    todoTasks: 0,
    completedThisWeek: 0,
    upcomingDueTasks: 0
  });
  const [users, setUsers] = useState<{ id: number; name: string; email: string }[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [refreshClicked, setRefreshClicked] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [activeTab, setActiveTab] = useState<'table' | 'kanban'>('table');
  const { currentUser } = useAuth();

  const fetchData = async () => {
    try {
      setLoading(true);
      setApiError(null);
      
      // Fetch tasks
      const tasksRes = await axiosInstance.get<Task[]>('/tasks');
      
      // Fetch users for assignment
      const usersRes = await axiosInstance.get<{ id: number; name: string; email: string; }[]>('/users');
      
      // Calculate stats
      const allTasks = tasksRes.data;
      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
      startOfWeek.setHours(0, 0, 0, 0);
      
      const taskStats: TaskStats = {
        totalTasks: allTasks.length,
        completedTasks: allTasks.filter(t => t.status === TaskStatus.DONE).length,
        inProgressTasks: allTasks.filter(t => t.status === TaskStatus.IN_PROGRESS).length,
        todoTasks: allTasks.filter(t => t.status === TaskStatus.TODO).length,
        completedThisWeek: allTasks.filter(t => 
          t.status === TaskStatus.DONE && 
          new Date(t.updatedAt || '') >= startOfWeek
        ).length,
        upcomingDueTasks: allTasks.filter(t => 
          t.status !== TaskStatus.DONE && 
          t.dueDate && 
          new Date(t.dueDate) <= new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
        ).length
      };
      
      setTasks(allTasks);
      setUsers(usersRes.data);
      setStats(taskStats);
      setLastUpdated(new Date());
    } catch (error: any) {
      console.error('Error fetching task data:', error);
      setApiError(error.response?.data?.message || t('common.errorOccurred'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshClicked(true);
    try {
      await fetchData();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setRefreshClicked(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Refresh data automatically every 60 seconds
    const interval = setInterval(() => {
      fetchData();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const validateForm = (data: Task) => {
    const newErrors: Record<string, string> = {};

    if (!data.title?.trim()) {
      newErrors.title = t('task.errors.titleRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (data: Task) => {
    if (!validateForm(data)) {
      return;
    }

    try {
      if (selectedTask?.id) {
        // S'assurer que la description est incluse dans la mise à jour
        await axiosInstance.put(`/tasks/${selectedTask.id}`, {
          ...data,
          description: data.description || '' // Assurer que description est définie
        });
      } else {
        await axiosInstance.post('/tasks', {
          ...data,
          description: data.description || '' // Assurer que description est définie
        });
      }
      await fetchData();
      setOpen(false);
      setSelectedTask(null);
    } catch (error: any) {
      console.error('Error saving task:', error);
      setApiError(error.response?.data?.error || t('common.errorOccurred'));
    }
  };
  
  const handleDelete = async (task: Task) => {
    if (window.confirm(t('task.confirmDelete'))) {
      try {
        await axiosInstance.delete(`/tasks/${task.id}`);
        fetchData();
      } catch (error) {
        console.error('Error deleting task:', error);
        setApiError(t('task.deleteError'));
      }
    }
  };

  const renderTaskCountCard = (title: string, count: number, total: number, icon: React.ReactNode, color: string) => (
    <Card className="bg-muted/30 hover:bg-muted/50 transition-colors">
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-muted-foreground text-sm">{title}</p>
            <h3 className="text-3xl font-bold">{count}</h3>
          </div>
          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${color}`}>
            {icon}
          </div>
        </div>
        <div className="mt-2">
          <Progress 
            value={total > 0 ? (count / total) * 100 : 0} 
            className="h-1 bg-muted/50"
          />
          <p className="text-xs text-muted-foreground mt-1">
            {total > 0 ? Math.round((count / total) * 100) : 0}% {t('task.ofTotal')}
          </p>
        </div>
      </CardContent>
    </Card>
  );

  if (loading && tasks.length === 0) {
    return (
      <div className="p-6 space-y-6">
        <Card className="border-none shadow-sm dark:shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              <CardTitle className="text-2xl">{t('task.management')}</CardTitle>
            </div>
            <CardDescription>{t('common.loading')}</CardDescription>
          </CardHeader>
          <CardContent className="pb-6">
            <div className="space-y-4">
              <Skeleton className="h-[150px] w-full" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="p-6 space-y-6">
        <Card className="border-none shadow-sm dark:shadow-lg">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-primary" />
                  <CardTitle className="text-2xl">{t('task.management')}</CardTitle>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <Alert variant="destructive">
              <AlertCircle className="h-5 w-5" />
              <AlertTitle>{t('common.error')}</AlertTitle>
              <AlertDescription>
                {apiError}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRefresh}
                  className="ml-2"
                >
                  {t('common.retry')}
                </Button>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Ajouter cette fonction manquante pour gérer le glisser-déposer des tâches
  const handleTaskDrop = async (taskId: number, newStatus: TaskStatus) => {
    try {
      const taskToUpdate = tasks.find(task => task.id === taskId);
      
      if (!taskToUpdate) return;
      
      // Mise à jour optimiste
      const updatedTasks = tasks.map(task => 
        task.id === taskId ? { ...task, status: newStatus } : task
      );
      setTasks(updatedTasks);
      
      await axiosInstance.put(`/tasks/${taskId}`, {
        ...taskToUpdate,
        status: newStatus
      });
      
      // Mettre à jour les statistiques après le changement de statut
      await fetchData();
      
    } catch (error) {
      console.error('Error updating task status:', error);
      setApiError(t('task.updateError'));
      // Revenir à l'état initial en cas d'erreur
      await fetchData();
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Main Status Card */}
      <Card className="border-none shadow-sm dark:shadow-lg">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-primary" />
                <CardTitle className="text-2xl">{t('task.management')}</CardTitle>
              </div>
              {lastUpdated && (
                <CardDescription className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {t('common.lastUpdated')}: {lastUpdated.toLocaleTimeString()}
                </CardDescription>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handleRefresh}
                disabled={loading || refreshClicked}
                className="h-9 w-9 rounded-full transition-all"
                aria-label={t('common.refresh')}
              >
                <RefreshCw className={`h-4 w-4 ${(loading || refreshClicked) ? 'animate-spin text-primary' : ''}`} />
                <span className="sr-only">{t('common.refresh')}</span>
              </Button>
              <Button 
                onClick={() => {
                  setSelectedTask(null);
                  setErrors({});
                  setOpen(true);
                }}
                className="h-9 bg-primary/90 hover:bg-primary transition-colors"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('task.addNew')}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6 pt-4">
          {/* Task Statistics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {renderTaskCountCard(
              t('task.todoTasks'),
              stats.todoTasks,
              stats.totalTasks,
              <ClipboardList className="h-6 w-6 text-white" />,
              'bg-blue-500/90'
            )}
            
            {renderTaskCountCard(
              t('task.inProgressTasks'),
              stats.inProgressTasks,
              stats.totalTasks,
              <Loader className="h-6 w-6 text-white" />,
              'bg-amber-500/90'
            )}
            
            {renderTaskCountCard(
              t('task.completedTasks'),
              stats.completedTasks,
              stats.totalTasks,
              <CheckSquare className="h-6 w-6 text-white" />,
              'bg-green-500/90'
            )}
            
            {renderTaskCountCard(
              t('task.upcomingDueTasks'),
              stats.upcomingDueTasks,
              stats.totalTasks,
              <Calendar className="h-6 w-6 text-white" />,
              'bg-red-500/90'
            )}
          </div>

          {/* Weekly Progress */}
          <Card className="border-none shadow-sm bg-muted/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="h-5 w-5 text-muted-foreground" />
                {t('task.weeklyProgress')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="flex flex-col gap-4">
                <div className="flex justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">{t('task.completedThisWeek')}</p>
                    <p className="text-2xl font-bold">{stats.completedThisWeek}</p>
                  </div>
                  <div className="space-y-1 text-right">
                    <p className="text-sm font-medium">{t('task.completion')}</p>
                    <p className="text-2xl font-bold">
                      {stats.totalTasks > 0 ? Math.round((stats.completedTasks / stats.totalTasks) * 100) : 0}%
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">0%</span>
                    <span className="text-sm font-medium">
                      {stats.totalTasks > 0 ? Math.round((stats.completedTasks / stats.totalTasks) * 100) : 0}%
                    </span>
                    <span className="text-sm text-muted-foreground">100%</span>
                  </div>
                  <Progress 
                    value={stats.totalTasks > 0 ? (stats.completedTasks / stats.totalTasks) * 100 : 0} 
                    className="h-2"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {/* Tasks Table/Kanban Card */}
      <Card className="border-none shadow-sm dark:shadow-lg">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              <CardTitle className="text-xl">{t('task.assignedTasks')}</CardTitle>
            </div>
            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as 'table' | 'kanban')}
            >
              <TabsList>
                <TabsTrigger value="table">
                  {t('task.tableView')}
                </TabsTrigger>
                <TabsTrigger value="kanban">
                  {t('task.kanbanView')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          <Tabs value={activeTab}>
            <TabsContent value="table" className="m-0 pt-4">
              <div className="bg-muted/20 backdrop-blur-[1px] rounded-lg p-0.5 border border-border/40 shadow-sm">
                <TaskTable 
                  tasks={tasks}
                  onEdit={(task) => {
                    setSelectedTask(task);
                    setOpen(true);
                  }}
                  onDelete={handleDelete}
                  onStatusChange={async (task, newStatus) => {
                    try {
                      await axiosInstance.put(`/tasks/${task.id}`, {
                        ...task,
                        status: newStatus
                      });
                      await fetchData();
                    } catch (error) {
                      console.error('Error updating task status:', error);
                      setApiError(t('task.updateError'));
                    }
                  }}
                />
              </div>
            </TabsContent>
            
            <TabsContent value="kanban" className="m-0 pt-4">
              <TaskKanban 
                tasks={tasks} 
                onTaskDrop={handleTaskDrop}
                onEdit={(task) => {
                  setSelectedTask(task);
                  setOpen(true);
                }}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Task Edit Dialog */}
      <TaskEditDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setSelectedTask(null);
          setErrors({});
        }}
        onSubmit={handleSubmit}
        task={selectedTask}
        errors={errors}
        users={users}
        currentUserRole={currentUser?.role} // Passer le rôle de l'utilisateur actuel
      />
    </div>
  );
};
